import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import AdminShell from "../components/AdminShell";
import AdminCountryPicker from "../components/AdminCountryPicker";
import { COUNTRIES } from "../data/countriesList";
import {
  deleteAdminCountryCurrency,
  deleteAdminSubscriptionPlan,
  getAdminCountryCurrencies,
  getAdminJobPrices,
  getAdminSubscriptionPlans,
  isApiConfigured,
  patchAdminSubscriptionPlan,
  postAdminSubscriptionPlan,
  putAdminCountryCurrency,
  putAdminJobPrices,
} from "../apiClient";

const CURRENCY_LABELS = {
  USD: "US Dollar (default)",
  GBP: "Pound Sterling",
  EUR: "Euro",
  AUD: "Australian Dollar",
  CAD: "Canadian Dollar",
};

function toMoneyString(value) {
  if (value == null || value === "") return "";
  const n = Number(value);
  if (!Number.isFinite(n)) return "";
  return String(n);
}

function countryLabel(code) {
  return COUNTRIES.find((c) => c.value === code)?.label || code;
}

function compareCountryMappings(a, b) {
  return countryLabel(a.countryCode).localeCompare(countryLabel(b.countryCode), undefined, {
    sensitivity: "base",
  });
}

function mapAdminPlanRow(plan) {
  return {
    id: plan.id,
    name: plan.name,
    jobsPerMonth: String(plan.jobsPerMonth),
    isActive: plan.isActive !== false,
    isPopular: plan.isPopular === true,
    sortOrder: String(plan.sortOrder ?? 0),
  };
}

function SectionFeedback({ error, success, warnings }) {
  return (
    <>
      {error ? (
        <p className="jw-admin__error" role="alert">
          {error}
        </p>
      ) : null}
      {success ? (
        <p className="jw-admin__success" role="status">
          {success}
        </p>
      ) : null}
      {Array.isArray(warnings) && warnings.length > 0 ? (
        <ul className="jw-admin__warnings">
          {warnings.map((message) => (
            <li key={message}>{message}</li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

export default function AdminPricingPage({ currentUser, authToken, onSignOut }) {
  const [jobLoading, setJobLoading] = useState(true);
  const [jobSaving, setJobSaving] = useState(false);
  const [jobError, setJobError] = useState("");
  const [jobSuccess, setJobSuccess] = useState("");
  const [jobWarnings, setJobWarnings] = useState([]);
  const [jobPrices, setJobPrices] = useState([]);

  const [plansLoading, setPlansLoading] = useState(true);
  const [plansSaving, setPlansSaving] = useState(false);
  const [plansError, setPlansError] = useState("");
  const [plansSuccess, setPlansSuccess] = useState("");
  const [plansWarnings, setPlansWarnings] = useState([]);
  const [plans, setPlans] = useState([]);
  const [newPlanName, setNewPlanName] = useState("");
  const [newPlanJobs, setNewPlanJobs] = useState("10");
  const [addingPlan, setAddingPlan] = useState(false);

  const [countriesLoading, setCountriesLoading] = useState(true);
  const [countriesSaving, setCountriesSaving] = useState(false);
  const [countriesError, setCountriesError] = useState("");
  const [countriesSuccess, setCountriesSuccess] = useState("");
  const [mappings, setMappings] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState(["USD"]);
  const [newCountryCode, setNewCountryCode] = useState("");
  const [newCountryCurrency, setNewCountryCurrency] = useState("USD");
  const [mappingListSearch, setMappingListSearch] = useState("");

  const token = String(authToken || "").trim();

  const loadJobPrices = useCallback(async () => {
    if (!token || !isApiConfigured()) {
      setJobError("Not signed in.");
      setJobPrices([]);
      setJobLoading(false);
      return;
    }
    setJobLoading(true);
    setJobError("");
    setJobSuccess("");
    try {
      const data = await getAdminJobPrices(token);
      setJobWarnings(Array.isArray(data.warnings) ? data.warnings : []);
      setJobPrices(
        Array.isArray(data.prices)
          ? data.prices.map((row) => ({
              currencyCode: row.currencyCode,
              oneOffAmount: toMoneyString(row.oneOffAmount),
              subscriptionJobAmount: toMoneyString(row.subscriptionJobAmount),
              isActive: row.isActive !== false,
            }))
          : []
      );
    } catch (e) {
      setJobError(e instanceof Error ? e.message : "Could not load job pricing.");
      setJobPrices([]);
    } finally {
      setJobLoading(false);
    }
  }, [token]);

  const loadPlans = useCallback(async () => {
    if (!token || !isApiConfigured()) {
      setPlansError("Not signed in.");
      setPlans([]);
      setPlansLoading(false);
      return;
    }
    setPlansLoading(true);
    setPlansError("");
    setPlansSuccess("");
    try {
      const data = await getAdminSubscriptionPlans(token);
      setPlansWarnings(Array.isArray(data.warnings) ? data.warnings : []);
      setPlans(
        Array.isArray(data.plans)
          ? data.plans.map(mapAdminPlanRow)
          : []
      );
    } catch (e) {
      setPlansError(e instanceof Error ? e.message : "Could not load subscription packages.");
      setPlans([]);
    } finally {
      setPlansLoading(false);
    }
  }, [token]);

  const loadCountryMappings = useCallback(async () => {
    if (!token || !isApiConfigured()) {
      setCountriesError("Not signed in.");
      setMappings([]);
      setCountriesLoading(false);
      return;
    }
    setCountriesLoading(true);
    setCountriesError("");
    setCountriesSuccess("");
    try {
      const data = await getAdminCountryCurrencies(token);
      setMappings(Array.isArray(data.mappings) ? data.mappings : []);
      setCurrencyOptions(
        Array.isArray(data.currencyOptions) && data.currencyOptions.length > 0
          ? data.currencyOptions
          : ["USD"]
      );
    } catch (e) {
      setCountriesError(e instanceof Error ? e.message : "Could not load country mappings.");
      setMappings([]);
    } finally {
      setCountriesLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void loadJobPrices();
    void loadPlans();
    void loadCountryMappings();
  }, [loadJobPrices, loadPlans, loadCountryMappings]);

  const mappedCountryCodes = useMemo(
    () => mappings.map((m) => m.countryCode),
    [mappings]
  );

  const filteredMappings = useMemo(() => {
    const q = mappingListSearch.trim().toLowerCase();
    const list = q
      ? mappings.filter((row) => {
          const label = countryLabel(row.countryCode).toLowerCase();
          return (
            label.includes(q) ||
            row.countryCode.toLowerCase().includes(q) ||
            row.currencyCode.toLowerCase().includes(q)
          );
        })
      : mappings;
    return [...list].sort(compareCountryMappings);
  }, [mappings, mappingListSearch]);

  if (currentUser?.role !== "admin") {
    return <Navigate to="/admin/signin" replace />;
  }

  const handleJobPriceChange = (currencyCode, field, value) => {
    setJobSuccess("");
    setJobPrices((prev) =>
      prev.map((row) => (row.currencyCode === currencyCode ? { ...row, [field]: value } : row))
    );
  };

  const handleSaveJobPrices = async (event) => {
    event.preventDefault();
    if (!token || jobSaving) return;
    setJobSaving(true);
    setJobError("");
    setJobSuccess("");
    try {
      const payload = jobPrices.map((row) => ({
        currencyCode: row.currencyCode,
        oneOffAmount: Number(row.oneOffAmount),
        subscriptionJobAmount: Number(row.subscriptionJobAmount),
        isActive: row.isActive,
      }));
      const data = await putAdminJobPrices(token, payload);
      setJobWarnings(Array.isArray(data.warnings) ? data.warnings : []);
      setJobPrices(
        Array.isArray(data.prices)
          ? data.prices.map((row) => ({
              currencyCode: row.currencyCode,
              oneOffAmount: toMoneyString(row.oneOffAmount),
              subscriptionJobAmount: toMoneyString(row.subscriptionJobAmount),
              isActive: row.isActive !== false,
            }))
          : []
      );
      setJobSuccess("Job pricing saved.");
      await loadCountryMappings();
    } catch (e) {
      setJobError(e instanceof Error ? e.message : "Could not save job pricing.");
    } finally {
      setJobSaving(false);
    }
  };

  const handlePlanFieldChange = (planId, field, value) => {
    setPlansSuccess("");
    setPlans((prev) =>
      prev.map((plan) => (plan.id === planId ? { ...plan, [field]: value } : plan))
    );
  };

  const handlePlanPopularChange = (planId, checked) => {
    setPlansSuccess("");
    setPlans((prev) =>
      prev.map((plan) => {
        if (plan.id === planId) {
          return { ...plan, isPopular: checked };
        }
        if (checked) {
          return { ...plan, isPopular: false };
        }
        return plan;
      })
    );
  };

  const handleSavePlan = async (planId) => {
    if (!token || plansSaving) return;
    const plan = plans.find((p) => p.id === planId);
    if (!plan) return;
    setPlansSaving(true);
    setPlansError("");
    setPlansSuccess("");
    try {
      const data = await patchAdminSubscriptionPlan(token, planId, {
        name: plan.name,
        jobsPerMonth: Number(plan.jobsPerMonth),
        isActive: plan.isActive,
        isPopular: plan.isPopular,
        sortOrder: Number(plan.sortOrder) || 0,
      });
      setPlansWarnings(Array.isArray(data.warnings) ? data.warnings : []);
      setPlans(
        Array.isArray(data.plans)
          ? data.plans.map(mapAdminPlanRow)
          : []
      );
      setPlansSuccess("Package updated.");
    } catch (e) {
      setPlansError(e instanceof Error ? e.message : "Could not update that package.");
    } finally {
      setPlansSaving(false);
    }
  };

  const handleDeletePlan = async (planId) => {
    if (!token || plansSaving) return;
    const plan = plans.find((p) => p.id === planId);
    if (!plan) return;
    const label = String(plan.name || "").trim() || "this package";
    if (
      !window.confirm(
        `Delete "${label}"? This can't be undone — hiring managers won't be able to pick it anymore.`
      )
    ) {
      return;
    }
    setPlansSaving(true);
    setPlansError("");
    setPlansSuccess("");
    try {
      const data = await deleteAdminSubscriptionPlan(token, planId);
      setPlansWarnings(Array.isArray(data.warnings) ? data.warnings : []);
      setPlans(
        Array.isArray(data.plans)
          ? data.plans.map(mapAdminPlanRow)
          : []
      );
      setPlansSuccess("Package deleted.");
    } catch (e) {
      setPlansError(e instanceof Error ? e.message : "Could not delete that package.");
    } finally {
      setPlansSaving(false);
    }
  };

  const handleAddPlan = async (event) => {
    event.preventDefault();
    if (!token || addingPlan) return;
    setAddingPlan(true);
    setPlansError("");
    setPlansSuccess("");
    try {
      const data = await postAdminSubscriptionPlan(token, {
        name: newPlanName,
        jobsPerMonth: Number(newPlanJobs),
      });
      setPlansWarnings(Array.isArray(data.warnings) ? data.warnings : []);
      setPlans(
        Array.isArray(data.plans)
          ? data.plans.map(mapAdminPlanRow)
          : []
      );
      setNewPlanName("");
      setNewPlanJobs("10");
      setPlansSuccess("Package added.");
    } catch (e) {
      setPlansError(e instanceof Error ? e.message : "Could not add that package.");
    } finally {
      setAddingPlan(false);
    }
  };

  const handleAddCountryMapping = async (event) => {
    event.preventDefault();
    if (!token || countriesSaving || !newCountryCode) return;
    setCountriesSaving(true);
    setCountriesError("");
    setCountriesSuccess("");
    try {
      const data = await putAdminCountryCurrency(token, {
        countryCode: newCountryCode,
        currencyCode: newCountryCurrency,
      });
      setMappings(Array.isArray(data.mappings) ? data.mappings : []);
      setCurrencyOptions(
        Array.isArray(data.currencyOptions) && data.currencyOptions.length > 0
          ? data.currencyOptions
          : currencyOptions
      );
      setNewCountryCode("");
      setCountriesSuccess("Country mapping saved.");
    } catch (e) {
      setCountriesError(e instanceof Error ? e.message : "Could not save that country mapping.");
    } finally {
      setCountriesSaving(false);
    }
  };

  const handleRemoveCountryMapping = async (countryCode) => {
    if (!token || countriesSaving) return;
    setCountriesSaving(true);
    setCountriesError("");
    setCountriesSuccess("");
    try {
      const data = await deleteAdminCountryCurrency(token, countryCode);
      setMappings(Array.isArray(data.mappings) ? data.mappings : []);
      setCountriesSuccess("Country mapping removed.");
    } catch (e) {
      setCountriesError(e instanceof Error ? e.message : "Could not remove that country mapping.");
    } finally {
      setCountriesSaving(false);
    }
  };

  return (
    <AdminShell currentUser={currentUser} onSignOut={onSignOut}>
      <Link to="/admin" className="jw-admin__back">
        ← Back to home
      </Link>
      <h1 className="jw-admin__title">App Pricing Management and Subscriptions</h1>
      <p className="jw-admin__lede">
        Set what hiring managers pay for job postings — pay as you go, subscription packages, and which
        currency each country uses.
      </p>

      <section className="jw-admin-section" aria-labelledby="admin-job-pricing-heading">
        <h2 id="admin-job-pricing-heading" className="jw-admin-section__title">
          Pricing per job
        </h2>
        <p className="jw-admin-section__lede">
          Pay as you go is the price for a single job with no subscription. Subscription per-job is what we charge when
          someone on a plan goes over their monthly allowance.
        </p>
        <SectionFeedback error={jobError} success={jobSuccess} warnings={jobWarnings} />
        {jobLoading ? <p className="jw-admin__muted">Loading job pricing…</p> : null}
        {!jobLoading && jobPrices.length === 0 ? (
          <div className="jw-admin-section__placeholder">
            <p className="jw-admin__muted">No job pricing saved yet — add your first prices below.</p>
          </div>
        ) : null}
        {!jobLoading && jobPrices.length > 0 ? (
          <form className="jw-admin-rates" onSubmit={(event) => void handleSaveJobPrices(event)}>
            <div className="jw-admin-rates__table-wrap">
              <table className="jw-admin-rates__table">
                <thead>
                  <tr>
                    <th scope="col">Currency</th>
                    <th scope="col">Code</th>
                    <th scope="col">Pay as you go</th>
                    <th scope="col">Subscription per job</th>
                    <th scope="col">Active</th>
                  </tr>
                </thead>
                <tbody>
                  {jobPrices.map((row) => (
                    <tr key={row.currencyCode}>
                      <td>{CURRENCY_LABELS[row.currencyCode] || row.currencyCode}</td>
                      <td>
                        <code>{row.currencyCode}</code>
                      </td>
                      <td>
                        <input
                          type="number"
                          className="jw-admin-rates__input"
                          min="0"
                          step="0.01"
                          value={row.oneOffAmount}
                          aria-label={`Pay as you go price for ${row.currencyCode}`}
                          onChange={(event) =>
                            handleJobPriceChange(row.currencyCode, "oneOffAmount", event.target.value)
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="number"
                          className="jw-admin-rates__input"
                          min="0"
                          step="0.01"
                          value={row.subscriptionJobAmount}
                          aria-label={`Subscription per-job price for ${row.currencyCode}`}
                          onChange={(event) =>
                            handleJobPriceChange(
                              row.currencyCode,
                              "subscriptionJobAmount",
                              event.target.value
                            )
                          }
                        />
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          className="jw-admin-rates__checkbox"
                          checked={row.isActive}
                          aria-label={`Active pricing for ${row.currencyCode}`}
                          onChange={(event) =>
                            handleJobPriceChange(row.currencyCode, "isActive", event.target.checked)
                          }
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="jw-admin-rates__actions">
              <button type="submit" className="jw-admin__refresh" disabled={jobSaving}>
                {jobSaving ? "Saving…" : "Save job pricing"}
              </button>
              <button
                type="button"
                className="jw-admin__signout"
                disabled={jobLoading || jobSaving}
                onClick={() => void loadJobPrices()}
              >
                Reset
              </button>
            </div>
          </form>
        ) : null}
      </section>

      <section className="jw-admin-section" aria-labelledby="admin-subscription-plans-heading">
        <h2 id="admin-subscription-plans-heading" className="jw-admin-section__title">
          Subscriptions
        </h2>
        <p className="jw-admin-section__lede">
          Each package includes a set number of jobs per month. If someone posts more than that, we charge
          the subscription per-job rate from the table above.
        </p>
        <p className="jw-admin__muted">
          Most popular shows the badge on the public pricing page. Only one package at a time.
        </p>
        <SectionFeedback error={plansError} success={plansSuccess} warnings={plansWarnings} />
        {plansLoading ? <p className="jw-admin__muted">Loading subscription packages…</p> : null}
        {!plansLoading && plans.length === 0 ? (
          <div className="jw-admin-section__placeholder">
            <p className="jw-admin__muted">No subscription packages yet — add your first one below.</p>
          </div>
        ) : null}
        {!plansLoading && plans.length > 0 ? (
          <div className="jw-admin-rates__table-wrap">
            <table className="jw-admin-rates__table">
              <thead>
                <tr>
                  <th scope="col">Package name</th>
                  <th scope="col">Jobs included / month</th>
                  <th scope="col">Most popular</th>
                  <th scope="col">Active</th>
                  <th scope="col">Actions</th>
                </tr>
              </thead>
              <tbody>
                {plans.map((plan) => (
                  <tr key={plan.id}>
                    <td>
                      <input
                        type="text"
                        className="jw-admin-rates__input jw-admin-rates__input--wide"
                        value={plan.name}
                        aria-label="Package name"
                        onChange={(event) => handlePlanFieldChange(plan.id, "name", event.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="number"
                        className="jw-admin-rates__input"
                        min="1"
                        step="1"
                        value={plan.jobsPerMonth}
                        aria-label="Jobs included per month"
                        onChange={(event) =>
                          handlePlanFieldChange(plan.id, "jobsPerMonth", event.target.value)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        className="jw-admin-rates__checkbox"
                        checked={plan.isPopular}
                        aria-label={`Most popular package ${plan.name}`}
                        onChange={(event) =>
                          handlePlanPopularChange(plan.id, event.target.checked)
                        }
                      />
                    </td>
                    <td>
                      <input
                        type="checkbox"
                        className="jw-admin-rates__checkbox"
                        checked={plan.isActive}
                        aria-label={`Active package ${plan.name}`}
                        onChange={(event) =>
                          handlePlanFieldChange(plan.id, "isActive", event.target.checked)
                        }
                      />
                    </td>
                    <td>
                      <div className="jw-admin-rates__row-actions">
                        <button
                          type="button"
                          className="jw-admin__refresh"
                          disabled={plansSaving}
                          onClick={() => void handleSavePlan(plan.id)}
                        >
                          Save
                        </button>
                        <button
                          type="button"
                          className="jw-admin__signout"
                          disabled={plansSaving}
                          onClick={() => void handleDeletePlan(plan.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
        <form className="jw-admin-add-row" onSubmit={(event) => void handleAddPlan(event)}>
          <h3 className="jw-admin-add-row__title">Add a package</h3>
          <div className="jw-admin-add-row__fields">
            <label className="jw-admin-add-row__field">
              <span className="jw-admin-add-row__label">Package name</span>
              <input
                type="text"
                className="jw-admin-rates__input jw-admin-rates__input--wide"
                value={newPlanName}
                required
                onChange={(event) => setNewPlanName(event.target.value)}
              />
            </label>
            <label className="jw-admin-add-row__field">
              <span className="jw-admin-add-row__label">Jobs / month</span>
              <input
                type="number"
                className="jw-admin-rates__input"
                min="1"
                step="1"
                value={newPlanJobs}
                required
                onChange={(event) => setNewPlanJobs(event.target.value)}
              />
            </label>
            <button type="submit" className="jw-admin__refresh" disabled={addingPlan}>
              {addingPlan ? "Adding…" : "Add package"}
            </button>
          </div>
        </form>
      </section>

      <section className="jw-admin-section" aria-labelledby="admin-country-currencies-heading">
        <h2 id="admin-country-currencies-heading" className="jw-admin-section__title">
          Countries + currencies
        </h2>
        <p className="jw-admin-section__lede">
          Pick which currency we use for each country. Countries not listed here use USD by default.
        </p>
        <SectionFeedback error={countriesError} success={countriesSuccess} />
        {countriesLoading ? <p className="jw-admin__muted">Loading country mappings…</p> : null}
        {!countriesLoading ? (
          <form
            className="jw-admin-add-row jw-admin-add-row--first"
            onSubmit={(event) => void handleAddCountryMapping(event)}
          >
            <h3 className="jw-admin-add-row__title">Add a country</h3>
            <div className="jw-admin-add-row__fields">
              <label className="jw-admin-add-row__field">
                <span className="jw-admin-add-row__label">Country</span>
                <AdminCountryPicker
                  value={newCountryCode}
                  excludedCodes={mappedCountryCodes}
                  disabled={countriesSaving}
                  inputId="admin-pricing-country-picker"
                  onChange={setNewCountryCode}
                />
              </label>
              <label className="jw-admin-add-row__field">
                <span className="jw-admin-add-row__label">Currency</span>
                <select
                  className="jw-admin__filter-select"
                  value={newCountryCurrency}
                  required
                  onChange={(event) => setNewCountryCurrency(event.target.value)}
                >
                  {currencyOptions.map((code) => (
                    <option key={code} value={code}>
                      {CURRENCY_LABELS[code] || code} ({code})
                    </option>
                  ))}
                </select>
              </label>
              <button
                type="submit"
                className="jw-admin__refresh"
                disabled={countriesSaving || !newCountryCode}
              >
                {countriesSaving ? "Saving…" : "Add country"}
              </button>
            </div>
          </form>
        ) : null}
        {!countriesLoading && mappings.length > 0 ? (
          <>
            <label className="jw-admin-mappings-filter">
              <span className="jw-admin-add-row__label">Search countries in the list</span>
              <input
                type="search"
                className="jw-admin-rates__input jw-admin-rates__input--wide"
                value={mappingListSearch}
                placeholder="Filter by country or currency…"
                onChange={(event) => setMappingListSearch(event.target.value)}
              />
            </label>
            <div className="jw-admin-rates__table-wrap">
              <table className="jw-admin-rates__table">
                <thead>
                  <tr>
                    <th scope="col">Country</th>
                    <th scope="col">Code</th>
                    <th scope="col">Currency</th>
                    <th scope="col">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMappings.map((row) => (
                    <tr key={row.countryCode}>
                      <td>{countryLabel(row.countryCode)}</td>
                      <td>
                        <code>{row.countryCode}</code>
                      </td>
                      <td>
                        <code>{row.currencyCode}</code>
                      </td>
                      <td>
                        <button
                          type="button"
                          className="jw-admin__signout"
                          disabled={countriesSaving}
                          onClick={() => void handleRemoveCountryMapping(row.countryCode)}
                        >
                          Remove
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filteredMappings.length === 0 && mappingListSearch.trim() ? (
              <p className="jw-admin__muted">No countries match that search.</p>
            ) : null}
          </>
        ) : null}
        {!countriesLoading && mappings.length === 0 ? (
          <div className="jw-admin-section__placeholder">
            <p className="jw-admin__muted">No country mappings yet — everyone will use USD until you add one.</p>
          </div>
        ) : null}
      </section>
    </AdminShell>
  );
}
